import { partyLedger, type SaleEntry, type Paisa } from '@khata/core';
import { dLong, num, qty, taka } from '../lib/format.js';
import { t } from '../lib/i18n.js';
import { entries, master, shopName, state } from '../lib/khata.js';
import { itemName, partyName } from '../lib/labels.js';
import { go } from '../lib/router.js';

/** Printable cash memo for one sale. A5 portrait, also fine on a 80mm thermal roll. */
export function Memo(props: { id: string }) {
  const m = master.value;
  const e = entries.value.find((x) => x.id === props.id);
  if (!e || e.type !== 'sale') {
    return (
      <div class="memo">
        <p>{t('memo_missing')}</p>
        <button type="button" class="btn btn-ghost no-print" onClick={() => go('sales')}>
          {t('memo_back')}
        </button>
      </div>
    );
  }
  const sale: SaleEntry = e;
  const party = sale.partyId ? m.parties.get(sale.partyId) : undefined;
  const due = (sale.total - sale.paid) as Paisa;
  // Balance before and after this memo, from the party's ledger.
  let before: Paisa = 0 as Paisa;
  let after: Paisa = 0 as Paisa;
  if (party) {
    const rows = partyLedger(state.value, m, entries.value, party.id);
    const i = rows.findIndex((r) => r.entry.id === sale.id);
    if (i >= 0) {
      after = rows[i]!.running;
      before = (after - rows[i]!.delta) as Paisa;
    }
  }
  return (
    <div class="memo">
      <div class="no-print" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button type="button" class="btn btn-primary" onClick={() => window.print()}>
          {t('memo_print')}
        </button>
        <button type="button" class="btn btn-ghost" onClick={() => history.back()}>
          {t('memo_back')}
        </button>
      </div>
      <div class="memo-head">
        <div class="memo-shop">{shopName.value || t('appName')}</div>
        <div class="memo-meta">
          <div>
            {t('memo_memo')} <strong>#{num(sale.no)}</strong>
          </div>
          <div>{dLong(sale.date)}</div>
        </div>
      </div>
      <div class="memo-party">
        <div>
          <span class="muted">{t('memo_customer')}</span> {partyName(m, sale.partyId, t('walkIn'))}
          {party?.phone ? ` · ${party.phone}` : ''}
        </div>
        {party?.note && <div class="muted small">{party.note}</div>}
      </div>
      <table class="memo-table">
        <thead>
          <tr>
            <th>#</th>
            <th>{t('memo_item')}</th>
            <th class="r">{t('memo_qty')}</th>
            <th class="r">{t('memo_price')}</th>
            <th class="r">{t('memo_amount')}</th>
          </tr>
        </thead>
        <tbody>
          {sale.lines.map((l, i) => (
            <tr key={l.itemId}>
              <td>{num(i + 1)}</td>
              <td>{itemName(m, l.itemId)}</td>
              <td class="r">
                {qty(l.qty)} {m.items.get(l.itemId)?.unit ?? ''}
              </td>
              <td class="r">{taka(l.unitPrice)}</td>
              <td class="r">{taka(l.amount)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4} class="r">
              {t('memo_total')}
            </td>
            <td class="r">
              <strong>{taka(sale.total)}</strong>
            </td>
          </tr>
          <tr>
            <td colSpan={4} class="r">
              {t('memo_paid')}
            </td>
            <td class="r">{taka(sale.paid)}</td>
          </tr>
          {due > 0 && (
            <tr>
              <td colSpan={4} class="r">
                {t('memo_due')}
              </td>
              <td class="r">{taka(due)}</td>
            </tr>
          )}
          {party && (
            <>
              <tr>
                <td colSpan={4} class="r">
                  {t('memo_previous')}
                </td>
                <td class="r">{taka(before)}</td>
              </tr>
              <tr>
                <td colSpan={4} class="r">
                  <strong>{t('memo_balanceNow')}</strong>
                </td>
                <td class="r">
                  <strong>{taka(after)}</strong>
                </td>
              </tr>
            </>
          )}
        </tfoot>
      </table>
      {sale.note && (
        <div class="small">
          {t('memo_note')} {sale.note}
        </div>
      )}
      <div class="memo-foot muted small">{t('memo_thanks')}</div>
    </div>
  );
}
